import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchConnectionStatus,
  fetchAccounts,
  fetchLocations,
  fetchReviews,
  clearError,
  googleReviewErrorMessage,
} from '../store/slices/googleReviewSlice';
import { deriveSyncBarState } from '../utils/googleReviewSync';
import { addNotification } from '../store/slices/notificationSlice';
import GoogleConnectEmptyState from '../components/google-review/GoogleConnectEmptyState';
import ReviewListHeader from '../components/google-review/ReviewListHeader';
import SyncStatus from '../components/google-review/SyncStatus';
import ReviewTabBar from '../components/google-review/ReviewTabBar';
import ReviewFilters from '../components/google-review/ReviewFilters';
import ReviewCard from '../components/google-review/ReviewCard';
import BulkActionsBar from '../components/google-review/BulkActionsBar';
import EditReplyModal from '../components/google-review/EditReplyModal';

/** How often to re-poll while a sync is running (§6 - the banner clears itself). */
const SYNC_POLL_MS = 4000;

const SyncEmptyState: React.FC<{ icon: string; title: string; body: string; iconClass?: string }> = ({
  icon,
  title,
  body,
  iconClass,
}) => (
  <div className="google-review-empty">
    <div className={`google-review-empty__icon ${iconClass ?? ''}`}>{icon}</div>
    <div className="google-review-empty__title">{title}</div>
    <div className="google-review-empty__body">{body}</div>
  </div>
);

const GoogleReviewManagerPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    isConnected,
    isLoading,
    isLoadingMore,
    reviews,
    total,
    page,
    activeTab,
    ratingFilter,
    includeRatingOnly,
    selectedReviewIds,
    accounts,
    selectedAccountId,
    selectedLocationId,
    currentEditReview,
    syncState,
    error,
    errorCode,
  } = useAppSelector((state) => state.googleReview);

  const barState = deriveSyncBarState(syncState);
  const isSyncing = barState === 'in_progress';

  // Show error notification whenever the slice sets an error
  useEffect(() => {
    if (error) {
      dispatch(addNotification({ type: 'error', message: googleReviewErrorMessage(errorCode, error, t) }));
      dispatch(clearError());
    }
  }, [error, errorCode, dispatch, t]);

  // On mount: check connection; surface + strip OAuth callback params
  // (?connected=1 on success, ?oauth_error=<reason> on cancel/failure).
  useEffect(() => {
    dispatch(fetchConnectionStatus());

    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('oauth_error');

    if (oauthError) {
      dispatch(addNotification({
        type: 'error',
        message: oauthError === 'access_denied'
          ? t('google_review_connect_cancelled')
          : t('google_review_connect_failed'),
      }));
    }

    if (params.has('connected') || params.has('oauth_error')) {
      params.delete('connected');
      params.delete('oauth_error');
      const search = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${search ? `?${search}` : ''}`,
      );
    }
  }, [dispatch, t]);

  // Once connected, load accounts automatically
  useEffect(() => {
    if (isConnected) {
      dispatch(fetchAccounts());
    }
  }, [isConnected, dispatch]);

  // Auto-select first account when accounts load
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      const firstAccountId = accounts[0]?.accountId;
      if (!firstAccountId) {
        return;
      }

      dispatch({ type: 'googleReview/setSelectedAccountId', payload: firstAccountId });
      dispatch(fetchLocations(firstAccountId));
    }
  }, [accounts, selectedAccountId, dispatch]);

  // Auto-select first location when locations load and fetch reviews
  const locations = useAppSelector((s) => s.googleReview.locations);
  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId) {
      const firstLocationId = locations[0]?.locationId;
      if (!firstLocationId) {
        return;
      }

      dispatch({ type: 'googleReview/setSelectedLocationId', payload: firstLocationId });
      dispatch(fetchReviews({ locationId: firstLocationId, rating: ratingFilter, includeRatingOnly }));
    }
  }, [locations, selectedLocationId, ratingFilter, includeRatingOnly, dispatch]);

  // While a sync runs, poll connection-status (carries fresh sync_state) and re-fetch accounts
  // so a cold start's locations surface as soon as the sweep writes them (§6). The auto-select
  // effects above then chain locations → reviews. Stops the moment the sync leaves in_progress.
  useEffect(() => {
    if (!isSyncing) {
      return;
    }

    const id = window.setInterval(() => {
      dispatch(fetchConnectionStatus());
      dispatch(fetchAccounts());
      if (selectedLocationId) {
        dispatch(fetchReviews({ locationId: selectedLocationId, rating: ratingFilter, includeRatingOnly }));
      }
    }, SYNC_POLL_MS);

    return () => window.clearInterval(id);
  }, [isSyncing, selectedLocationId, ratingFilter, includeRatingOnly, dispatch]);

  if (isLoading && !isConnected) {
    return (
      <div className="google-review-manager-page">
        <div className="loading-spinner-wrapper">
          <div className="loading-spinner" aria-label={t('loading_text')} />
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="google-review-manager-page">
        <GoogleConnectEmptyState />
      </div>
    );
  }

  const hasReviews = reviews.length > 0;
  // Always show the toolbar once locations exist, so the user can switch locations regardless of
  // sync state or whether the current location has reviews. Hidden only before the first sync,
  // when there is nothing to select yet.
  const showToolbar = accounts.length > 0;
  const hasMore = reviews.length < total;

  const handleLoadMore = () => {
    if (!selectedLocationId || isLoadingMore) return;
    dispatch(fetchReviews({
      locationId: selectedLocationId,
      filter: activeTab === 'all' ? undefined : activeTab,
      rating: ratingFilter,
      includeRatingOnly,
      page: page + 1,
    }));
  };

  // The SyncStatus bar carries the message in every state (§6, design correction #3), so a
  // centred card is only for the genuinely empty cases - never when reviews already exist.
  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="loading-spinner-wrapper">
          <div className="loading-spinner" aria-label={t('loading_text')} />
        </div>
      );
    }

    if (hasReviews) {
      return (
        <>
          {reviews.map((review) => (
            review.reviewId ? (
              <ReviewCard
                key={review.reviewId}
                review={review}
                isSelected={selectedReviewIds.includes(review.reviewId)}
              />
            ) : null
          ))}
          {hasMore && (
            <div className="review-list__load-more">
              <button
                type="button"
                className="review-list__load-more-btn"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('loading_text') : t('google_review_load_more')}
              </button>
            </div>
          )}
        </>
      );
    }

    if (isSyncing) {
      return (
        <SyncEmptyState
          icon=""
          iconClass="google-review-empty__icon--spinner"
          title={t('google_review_sync_in_progress_title')}
          body={t('google_review_empty_in_progress_body')}
        />
      );
    }

    if (barState === 'failed') {
      return (
        <SyncEmptyState
          icon="!"
          iconClass="google-review-empty__icon--error"
          title={t('google_review_sync_failed_title')}
          body={t('google_review_empty_failed_body')}
        />
      );
    }

    if (barState === 'never_synced') {
      return (
        <SyncEmptyState
          icon="★"
          title={t('google_review_empty_never_title')}
          body={t('google_review_empty_never_body')}
        />
      );
    }

    // Synced, but this location genuinely has no reviews (matching the active filters).
    return <p className="review-list__empty">{t('google_review_no_reviews')}</p>;
  };

  return (
    <div className="google-review-manager-page">
      <ReviewListHeader />
      <SyncStatus />
      {showToolbar && (
        <div className="review-toolbar">
          <ReviewTabBar />
          <ReviewFilters />
        </div>
      )}

      <div className="review-list">
        {renderBody()}
      </div>

      <BulkActionsBar />
      {currentEditReview && <EditReplyModal />}
    </div>
  );
};

export default GoogleReviewManagerPage;
